import { ImageProcessingState, PanoramaView } from "@nova/constants"
import {
    createMultifileUploadSlice,
    singleFileProgressStream,
    speedStream,
} from "@nova/slice-multifile-upload"
import { createSlice } from "@reduxjs/toolkit"
import { withRoot } from "@nova/with-root"
import { panoramaWarningProcessor } from "./PanoramaWarnings"
import { addFlightUsage } from "features/data-management/workspace/subscriptions/module"
import { createPanoramaImage } from "features/pubsub/entityActions/images/panorama"
import { PanoramaImage } from "@nova/protobuf"
import { dateAsNumber } from "@nova/serializer/src/client/util"
import sanitizedFileName from "tools/strings/sanitizeFileName"
import { runTask } from "@nova/webworker-pool"
import { type ImageMetadata, processImage } from "integrations/sensortool/processImage"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { IngestDataType } from "@nova/schemas"
import { createSpeedCallback } from "themes/default-tailwind/panels/GlobalUploadContainer"

type PanoramaMeta = {
    organizationID: string
    fireID: string
    flightID: string
    setID: string
    file: File
}

export type PanoramaState = {
    id: string // this is now the filename sanitized since protobuf
    s3Key?: string
    state: ImageProcessingState
    // warnings?: ImageWarnings
    deletedAt?: string
    colorData?: Uint8Array
    longitude?: number
    latitude?: number
    altitude?: number
    takenAt?: number
    unsupported?: boolean
    pitch?: number
    yaw?: number
    roll?: number
    focalLength?: number
    make?: string
    fileName?: string
    size?: number
    isPano?: boolean // true/false for DJI images, undefined for others at the moment.
    visible: boolean
    flightId?: string
}

const s3key = ({ organizationID, fireID, flightID, id }: PanoramaMeta & { id: string }) => {
    return `public/org/${organizationID}/fire/${fireID}/flight/${flightID}/images/${id}`
}

const {
    actions,
    reducer: panoramaReducer,
    selectors: {
        selectSet,
        selectUpload,
        selectAllUploads,
        selectUploadsForSet,
        selectUploadIdsForSet,
        selectCancelledUploadIdsForSet,
        selectAllSets,
        selectUploadsForSetSummary: baseSelectUploadsForSetSummary,
        selectPendingForSet: baseSelectPendingForSet,
    },
} = createMultifileUploadSlice<"panorama-v1", PanoramaState, PanoramaMeta>(
    "panorama-v1",
    (m: PanoramaMeta) =>
        ({
            id: sanitizedFileName(m.file.name),
            state: ImageProcessingState.Pending,
            visible: true,
        }) satisfies PanoramaState,
    {
        addUploadToProject: (setId, upload) =>
            createPanoramaImage({
                data: upload as PanoramaImage,
                context: { flightId: setId, name: upload.id },
            }),
    },
    async (state, meta, update, { useSignal, useSemaphore }) => {
        const uploadSignal = useSignal("upload", meta.setID)
        const deleteSignal = useSignal("delete", state.id)
        deleteSignal.then(() => {
            if (state.state !== ImageProcessingState.UploadDone)
                state = { ...state, deletedAt: new Date().toISOString() }
        })
        // semaphore lock - maximum 15 pending to make sure we don't overload the machine, RGB images take a bit to chunk through
        let metadata: ImageMetadata
        await useSemaphore(10, async () => {
            update({ ...state, state: ImageProcessingState.Processing })
            if (state.deletedAt) return
            // process the data
            const fileBuffer = await meta.file.arrayBuffer()
            metadata = await runTask(processImage, { fileBuffer }, { transfer: [fileBuffer] })

            const make = metadata?.make?.toLowerCase()
            const isPano = make === "dji" ? !!metadata?.tags?.["GPano"] : undefined

            state = {
                ...state,
                ...metadata?.gps,
                takenAt: dateAsNumber(metadata?.takenAt),
                state: ImageProcessingState.ProcessingDone,
                unsupported: !metadata?.isSupportedSensor,
                ...metadata?.pyr,
                focalLength: metadata?.focalLength,
                fileName: meta.file.name,
                size: meta.file.size,
                make,
                flightId: meta.setID,
                isPano,
                // warnings: metadata?.warnings, // Ignore all types of warnings for now.
            }
            update(state)
        })

        // this waits for the upload action to be fired by the UI
        if (state.deletedAt) return
        await uploadSignal
        if (state.deletedAt) return
        state = {
            ...state,
            state: ImageProcessingState.PendingUpload,
        }
        update(state)
        // semaphore lock - revisit https://en.wikipedia.org/wiki/Semaphore_(programming) before poking around it this
        // 5 is the number of uploads that are allowed to be in progress at once. As soon as one finishes, it'll start the next
        let ingestPromise: Promise<unknown> | undefined
        let hadIngestError = false
        await useSemaphore(5, async () => {
            if (state.deletedAt) return
            update({ ...state, state: ImageProcessingState.Uploading })

            const filename = state.id

            let s3Done!: () => void
            const s3Signal = new Promise<void>((r) => (s3Done = r))

            ingestPromise = fileManagementApi.putFile(meta.file, {
                filename,
                tags: {
                    dataType: IngestDataType.Panorama,
                    orgId: meta.organizationID,
                    projectId: meta.fireID,
                    flightId: meta.flightID,
                },
                onProgress: createSpeedCallback(({ speedMbps, percentUploaded }) => {
                    speedStream.setSpeed(meta.setID, state.id, speedMbps)
                    singleFileProgressStream.setProgress(meta.setID, percentUploaded / 100)
                }),
                onS3Complete: () => {
                    if (!state.deletedAt) {
                        update({
                            ...state,
                            state: ImageProcessingState.UploadDone,
                            s3Key: s3key({ ...meta, id: filename }),
                        })
                    }
                    s3Done()
                },
                onIngestError: (ingest) => {
                    hadIngestError = true
                    update({
                        ...state,
                        error: { name: "IngestError", message: `ingest ${ingest.status}` },
                    } as typeof state)
                },
            })

            // Release semaphore after S3 upload; if upload errors, release immediately via the race
            await Promise.race([s3Signal, ingestPromise.catch(() => {})])

            if (state.deletedAt) return
            actions.signal("delete", state.id)
        })

        if (ingestPromise) {
            await ingestPromise
            if (!state.deletedAt && !hadIngestError) {
                update({ ...state, state: ImageProcessingState.IngestDone })
            }
        }

        return { ...state }
    },
    panoramaWarningProcessor
)

export const stageFiles = (
    organizationID: string,
    fireID: string,
    flightID: string,
    files: File[],
    onFinish?: () => void
) => {
    const meta = new Array<PanoramaMeta>(files.length)
    let totalSize = 0
    for (let i = 0; i < files.length; i++) {
        meta[i] = {
            organizationID,
            fireID,
            flightID,
            setID: flightID,
            file: files[i],
        }
        totalSize += files[i].size
    }
    addFlightUsage(flightID, totalSize)
    return actions.stageFiles({ meta, onFinish })
}

export const uploadFlight = (id: string) => actions.signal("upload", id)
export const deleteUpload = actions.deleteUpload
export const deleteUploadSet = actions.deleteUploadSet

// this is summarizing the state of uploads for a set
// it's useful for determining if a set is uploading, how much of it is uploading, etc.
const selectUploadsForSetSummary = (id: string) =>
    baseSelectUploadsForSetSummary(
        id,
        (t) => t.state,
        (t) => t.state === ImageProcessingState.UploadDone
    )

const selectPendingUploadsForSet = (id: string) => baseSelectPendingForSet(id, "upload")

type UIState = {
    selectedUpload: Record<string, boolean>
    selectedView: PanoramaView
    downloadPending: boolean
}

const uiStateName = "panorama-ui-v1"
type uiStateNameType = typeof uiStateName

const initialState: UIState = {
    selectedUpload: {},
    selectedView: PanoramaView.Mini,
    downloadPending: false,
}

const {
    reducer: uiStateReducer,
    actions: { setSelectedUpload, setSelectedView, setDownloadPending },
} = createSlice({
    name: uiStateName,
    initialState,
    reducers: {
        setSelectedUpload: (state, action) => {
            state.selectedUpload = action.payload
        },
        setSelectedView: (state, action: { payload: PanoramaView }) => {
            state.selectedView = action.payload
        },
        setDownloadPending: (state, action: { payload: boolean }) => {
            state.downloadPending = action.payload
        },
    },
})

const { selectSelectedUpload, selectSelectedView, selectDownloadPending } = withRoot(
    (r: Record<uiStateNameType, UIState>) => r[uiStateName],
    {
        selectSelectedUpload: () => (s) => s.selectedUpload,
        selectSelectedView: () => (s) => s.selectedView,
        selectDownloadPending: () => (s) => s.downloadPending,
    }
)

export {
    selectSet,
    selectAllUploads,
    selectUpload,
    selectUploadsForSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectPendingUploadsForSet,
    selectUploadsForSetSummary,
    selectSelectedUpload,
    selectSelectedView,
    selectDownloadPending,
    selectAllSets,
}

export { setSelectedUpload, setSelectedView, setDownloadPending }

export const reducer = {
    ...panoramaReducer,
}

export const uiReducer = { [uiStateName]: uiStateReducer }
